import { normalizeErpUsername } from './erpUsername';
import { createCorrelationId, startPerformanceSpan } from './performanceTelemetry';

const ERP_API_BASE = '/api/erp/v1';
const ERP_DEVICE_KEY = 'pontomais.erp.device.v1';
const ERP_CSRF_KEY = 'pontomais.erp.csrf.v1';

export const ERP_SESSION_REPLACED_EVENT = 'pontomais:erp-session-replaced';
export const ERP_SESSION_REPLACED_MESSAGE = 'Este usuário fez login em outro dispositivo.';

let currentSession = null;
let refreshPromise = null;
let replacementNotified = false;

export class ErpSessionError extends Error {
  constructor(message, { status = 0, code = 'erp_session_error', details = null } = {}) {
    super(message);
    this.name = 'ErpSessionError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_error) {
    return null;
  }
}

function randomDeviceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `erp-web-${crypto.randomUUID()}`;
  }
  return `erp-web-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}

export function getErpDeviceId() {
  const storage = getSessionStorage();
  const saved = storage?.getItem(ERP_DEVICE_KEY);
  if (saved && saved.length >= 8 && saved.length <= 200) return saved;
  const created = randomDeviceId();
  storage?.setItem(ERP_DEVICE_KEY, created);
  return created;
}

function actorFromData(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.actor && typeof data.actor === 'object') return data.actor;
  if (data.tenant || data.user || data.session) {
    return {
      tenant: data.tenant || null,
      user: data.user || null,
      session: data.session || null
    };
  }
  return null;
}

function normalizeSession(payload) {
  const data = payload?.data || payload || {};
  const accessToken = data.accessToken || data.access_token || null;
  const csrfToken = data.csrfToken || data.csrf_token || null;
  if (!accessToken || !csrfToken) return null;
  return {
    accessToken,
    csrfToken,
    actor: actorFromData(data),
    heartbeatIntervalMs: Number(data.heartbeatIntervalMs || data.heartbeat_interval_ms || 0) || null
  };
}

function persistSession(session) {
  currentSession = session;
  const storage = getSessionStorage();
  if (!session?.csrfToken) {
    storage?.removeItem(ERP_CSRF_KEY);
    return;
  }
  storage?.setItem(ERP_CSRF_KEY, session.csrfToken);
  replacementNotified = false;
}

function errorCode(payload, status) {
  if (typeof payload?.error === 'string') return payload.error;
  return payload?.error?.code || payload?.code || `http_${status}`;
}

function errorMessage(payload, fallback) {
  return payload?.message
    || payload?.error?.message
    || payload?.error_description
    || fallback;
}

async function parseResponse(response) {
  const contentType = String(response.headers?.get?.('content-type') || '').toLowerCase();
  if (!contentType.includes('application/json')) return null;
  try {
    return await response.json();
  } catch (_error) {
    return null;
  }
}

function dispatchSessionReplaced() {
  currentSession = null;
  refreshPromise = null;
  getSessionStorage()?.removeItem(ERP_CSRF_KEY);
  if (replacementNotified || typeof window === 'undefined') return;
  replacementNotified = true;
  const detail = Object.freeze({ message: ERP_SESSION_REPLACED_MESSAGE });
  window.dispatchEvent(new CustomEvent(ERP_SESSION_REPLACED_EVENT, { detail }));
}

function responseError(response, payload, fallback) {
  return new ErpSessionError(errorMessage(payload, fallback), {
    status: response.status,
    code: errorCode(payload, response.status),
    details: payload?.error?.details || payload?.details || null
  });
}

async function transientAvailabilityRequest(path, options) {
  try {
    return await request(path, options);
  } catch (error) {
    const status = Number(error?.status || 0);
    if (status < 500 || status > 599) throw error;
    throw new ErpSessionError(
      'O servidor do ERP esta temporariamente indisponivel. Tente novamente em alguns instantes.',
      {
        status,
        code: 'network_unavailable',
        details: error?.details || null
      }
    );
  }
}

async function fetchResponse(path, options) {
  try {
    const response = await fetch(`${ERP_API_BASE}${path}`, options);
    return { response, payload: await parseResponse(response) };
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ErpSessionError(
      'O servidor do ERP esta temporariamente indisponivel. Tente novamente em alguns instantes.',
      { code: 'network_unavailable' }
    );
  }
}

async function request(path, {
  method = 'GET',
  body,
  authenticated = false,
  retryAfterRefresh = true,
  extraHeaders = null,
  signal,
  correlationId = createCorrelationId()
} = {}) {
  const finishRequest = startPerformanceSpan('api-request', {
    method,
    route: String(path || '').split('?')[0],
    correlationId
  });
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (authenticated && currentSession?.accessToken) {
    headers.Authorization = `Bearer ${currentSession.accessToken}`;
    headers['X-PontoMais-Device-Id'] = getErpDeviceId();
  }
  headers['X-Nivora-Correlation-Id'] = correlationId;
  Object.assign(headers, extraHeaders || {});

  let response;
  let payload;
  try {
    const result = await fetchResponse(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include',
      signal
    });
    response = result.response;
    payload = result.payload;
    finishRequest({ status: 'ok', httpStatus: response.status });
  } catch (error) {
    finishRequest({ status: 'error', errorCode: String(error?.code || error?.status || 'network_error') });
    throw error;
  }
  const code = errorCode(payload, response.status);

  // Esta resposta prova que outro login venceu. Renovar aqui criaria um loop
  // e esconderia do operador a razao real da queda da sessao.
  if (response.status === 401 && code === 'session_replaced') {
    dispatchSessionReplaced();
    throw responseError(response, payload, ERP_SESSION_REPLACED_MESSAGE);
  }

  if (response.status === 401 && authenticated && retryAfterRefresh) {
    await refreshErpSession();
    return request(path, {
      method,
      body,
      authenticated,
      retryAfterRefresh: false,
      extraHeaders,
      signal,
      correlationId
    });
  }

  if (!response.ok || payload?.ok === false) {
    throw responseError(
      response,
      payload,
      response.status >= 500 ? 'O servidor do ERP esta temporariamente indisponivel.' : 'Nao foi possivel concluir a solicitacao.'
    );
  }
  return payload?.data ?? payload;
}

async function requestBinary(path, { retryAfterRefresh = true } = {}) {
  const correlationId = createCorrelationId();
  const finishRequest = startPerformanceSpan('api-binary-request', {
    method: 'GET',
    route: String(path || '').split('?')[0],
    correlationId
  });
  const headers = { Accept: 'image/webp' };
  if (currentSession?.accessToken) {
    headers.Authorization = `Bearer ${currentSession.accessToken}`;
    headers['X-PontoMais-Device-Id'] = getErpDeviceId();
  }
  headers['X-Nivora-Correlation-Id'] = correlationId;
  let response;
  let payload;
  try {
    const result = await fetchResponse(path, {
      method: 'GET',
      headers,
      credentials: 'include'
    });
    response = result.response;
    payload = result.payload;
    finishRequest({ status: 'ok', httpStatus: response.status });
  } catch (error) {
    finishRequest({ status: 'error', errorCode: String(error?.code || error?.status || 'network_error') });
    throw error;
  }
  const code = errorCode(payload, response.status);
  if (response.status === 401 && code === 'session_replaced') {
    dispatchSessionReplaced();
    throw responseError(response, payload, ERP_SESSION_REPLACED_MESSAGE);
  }
  if (response.status === 401 && retryAfterRefresh) {
    await refreshErpSession();
    return requestBinary(path, { retryAfterRefresh: false });
  }
  if (!response.ok) {
    throw responseError(
      response,
      payload,
      response.status >= 500
        ? 'O servidor do ERP esta temporariamente indisponivel.'
        : 'Nao foi possivel carregar a imagem do produto.'
    );
  }
  return response.blob();
}

// API autenticada para telas de módulos do tenant. Mantém refresh, CSRF,
// device binding e tratamento de sessão centralizados neste cliente.
export function requestErpApi(path, options = {}) {
  return request(path, { ...options, authenticated: true });
}

function productMediaPath(productCode, suffix = '') {
  const code = String(productCode || '').normalize('NFKC').trim();
  const unsafeCharacter = Array.from(code).some((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint <= 31 || codePoint === 127 || character === '/' || character === '\\';
  });
  if (!code || code.length > 160 || unsafeCharacter) {
    throw new ErpSessionError('Informe um codigo de produto valido.', {
      code: 'invalid_product_code'
    });
  }
  return `/catalog/products/${encodeURIComponent(code)}/images${suffix}`;
}

export function listErpProductImages(productCode) {
  return request(productMediaPath(productCode), { authenticated: true });
}

export function uploadErpProductImage(productCode, payload = {}) {
  return request(productMediaPath(productCode), {
    method: 'POST',
    body: payload,
    authenticated: true
  });
}

export function replaceErpProductImage(productCode, imageId, payload = {}) {
  return request(productMediaPath(
    productCode,
    `/${encodeURIComponent(String(imageId || '').trim())}`
  ), {
    method: 'PUT',
    body: payload,
    authenticated: true
  });
}

export function updateErpProductImagesLayout(productCode, { order, coverId } = {}) {
  return request(productMediaPath(productCode), {
    method: 'PATCH',
    body: { order, coverId },
    authenticated: true
  });
}

export function deleteErpProductImage(productCode, imageId) {
  return request(productMediaPath(
    productCode,
    `/${encodeURIComponent(String(imageId || '').trim())}`
  ), {
    method: 'DELETE',
    authenticated: true
  });
}

export function rebindErpProductImages(fromProductCode, newProductCode, idempotencyKey) {
  return request(productMediaPath(fromProductCode, '/rebind'), {
    method: 'POST',
    body: { newProductCode },
    authenticated: true,
    extraHeaders: { 'X-Idempotency-Key': String(idempotencyKey || '').trim() }
  });
}

export function getErpProductImageBlob(productCode, imageId, variant = 'thumbnail') {
  const acceptedVariant = variant === 'full' ? 'full' : 'thumbnail';
  return requestBinary(productMediaPath(
    productCode,
    `/${encodeURIComponent(String(imageId || '').trim())}/${acceptedVariant}`
  ));
}

const ERP_CORREIOS_BASE = '/correios';

function correiosIdempotencyKey(operation, reference = '') {
  const suffix = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  const safeReference = String(reference || 'operation')
    .normalize('NFKC')
    .replace(/[^A-Za-z0-9._/-]+/g, '-')
    .slice(0, 80);
  return `correios:${operation}:${safeReference}:${suffix}`.slice(0, 200);
}

export function getErpCorreiosConfiguration() {
  return request(`${ERP_CORREIOS_BASE}/configuration`, { authenticated: true });
}

export function saveErpCorreiosConfiguration(payload = {}) {
  return request(`${ERP_CORREIOS_BASE}/configuration`, {
    method: 'PUT',
    body: payload,
    authenticated: true
  });
}

export function listErpCorreiosShipments(limit = 50) {
  const safeLimit = Math.min(100, Math.max(1, Number(limit) || 50));
  return request(`${ERP_CORREIOS_BASE}/shipments?limit=${safeLimit}`, {
    authenticated: true
  });
}

export function createErpCorreiosShipment(payload = {}) {
  const body = { ...payload };
  delete body.idempotencyKey;
  return request(`${ERP_CORREIOS_BASE}/shipments`, {
    method: 'POST',
    body,
    authenticated: true,
    extraHeaders: {
      'X-Idempotency-Key': String(payload.idempotencyKey || '').trim()
        || correiosIdempotencyKey('shipment', payload.orderReference)
    }
  });
}

export function requestErpCorreiosLabel(shipmentId, payload = {}) {
  const id = encodeURIComponent(String(shipmentId || '').trim());
  return request(`${ERP_CORREIOS_BASE}/shipments/${id}/labels`, {
    method: 'POST',
    body: {
      labelType: payload.labelType || 'P',
      labelFormat: 'ET'
    },
    authenticated: true,
    extraHeaders: {
      'X-Idempotency-Key': String(payload.idempotencyKey || '').trim()
        || correiosIdempotencyKey('label', shipmentId)
    }
  });
}

export function listErpCorreiosLabels(limit = 50) {
  const safeLimit = Math.min(100, Math.max(1, Number(limit) || 50));
  return request(`${ERP_CORREIOS_BASE}/labels?limit=${safeLimit}`, {
    authenticated: true
  });
}

export function consultErpCorreiosPrepostagens(query = {}) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim()) {
      params.set(key, String(value).trim());
    }
  });
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return request(`${ERP_CORREIOS_BASE}/prepostagens${suffix}`, {
    authenticated: true
  });
}

export function listErpCorreiosReverseRequests(limit = 50) {
  const safeLimit = Math.min(100, Math.max(1, Number(limit) || 50));
  return request(`${ERP_CORREIOS_BASE}/reverse?limit=${safeLimit}`, {
    authenticated: true
  });
}

export function createErpCorreiosReverseRequest(payload = {}) {
  const body = { ...payload };
  delete body.idempotencyKey;
  return request(`${ERP_CORREIOS_BASE}/reverse`, {
    method: 'POST',
    body,
    authenticated: true,
    extraHeaders: {
      'X-Idempotency-Key': String(payload.idempotencyKey || '').trim()
        || correiosIdempotencyKey('reverse', payload.orderReference)
    }
  });
}

export function refreshErpCorreiosReverseRequest(reverseId) {
  const id = encodeURIComponent(String(reverseId || '').trim());
  return request(`${ERP_CORREIOS_BASE}/reverse/${id}/refresh`, {
    method: 'POST',
    body: {},
    authenticated: true
  });
}

export function clearErpSession() {
  currentSession = null;
  refreshPromise = null;
  getSessionStorage()?.removeItem(ERP_CSRF_KEY);
}

export function hasErpSession() {
  return Boolean(currentSession?.accessToken);
}

export function peekErpSessionActor() {
  return currentSession?.actor || null;
}

export function isCloudAuthoritativeSession(userOrActor = peekErpSessionActor()) {
  if (!userOrActor || typeof userOrActor !== 'object') return false;
  const mode = userOrActor?.tenant?.persistenceMode
    || userOrActor?.persistence_mode
    || userOrActor?.persistenceMode;
  return String(mode || 'cloud_authoritative').trim().toLowerCase() === 'cloud_authoritative';
}

export function hasRecoverableErpSession() {
  return hasErpSession() || Boolean(getSessionStorage()?.getItem(ERP_CSRF_KEY));
}

export async function loginErpSession({
  tenantSlug,
  username,
  password,
  deviceLabel,
  clientKind
}) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: {
      tenantSlug: String(tenantSlug || '').trim().toLowerCase(),
      username: normalizeErpUsername(username),
      password,
      deviceId: getErpDeviceId(),
      deviceLabel: String(deviceLabel || (typeof navigator === 'undefined' ? 'Navegador' : navigator.userAgent)).slice(0, 120),
      clientKind: clientKind || (typeof window !== 'undefined' && window.electron?.runtime === 'electron' ? 'desktop' : 'web')
    }
  });
  const session = normalizeSession(data);
  if (!session) {
    throw new ErpSessionError('O servidor nao devolveu uma sessao central valida.', {
      code: 'invalid_session_response'
    });
  }
  persistSession(session);
  return session.actor;
}

export async function createTrialAccount({
  tenantSlug,
  tenantName,
  tenantDisplayName,
  businessProfile,
  ownerUsername,
  ownerDisplayName,
  ownerPassword,
  referralCode
} = {}) {
  return request('/auth/signup', {
    method: 'POST',
    body: {
      tenantSlug,
      tenantName,
      tenantDisplayName,
      businessProfile,
      ownerUsername: normalizeErpUsername(ownerUsername),
      ownerDisplayName,
      ownerPassword,
      referralCode
    }
  });
}

export function requestErpPasswordReset({ tenantSlug, username } = {}) {
  return request('/auth/password/forgot', {
    method: 'POST',
    body: {
      tenantSlug: String(tenantSlug || '').trim().toLowerCase(),
      username: normalizeErpUsername(username)
    }
  });
}

export function confirmErpPasswordReset({ token, newPassword } = {}) {
  return request('/auth/password/reset', {
    method: 'POST',
    body: {
      token: String(token || '').trim(),
      newPassword
    }
  });
}

async function performRefresh() {
  const csrfToken = currentSession?.csrfToken || getSessionStorage()?.getItem(ERP_CSRF_KEY);
  if (!csrfToken) {
    throw new ErpSessionError('Nao existe sessao central recuperavel.', { code: 'session_not_recoverable' });
  }
  const { response, payload } = await fetchResponse('/auth/refresh', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-PontoMais-CSRF': csrfToken,
      'X-PontoMais-Device-Id': getErpDeviceId()
    },
    credentials: 'include'
  });
  const code = errorCode(payload, response.status);
  if (response.status === 401 && code === 'session_replaced') {
    dispatchSessionReplaced();
    throw responseError(response, payload, ERP_SESSION_REPLACED_MESSAGE);
  }
  if (!response.ok || payload?.ok === false) {
    clearErpSession();
    throw responseError(response, payload, 'Sua sessao central expirou. Entre novamente.');
  }
  const session = normalizeSession(payload);
  if (!session) {
    clearErpSession();
    throw new ErpSessionError('Sua sessao central expirou. Entre novamente.', {
      code: 'invalid_session_response'
    });
  }
  persistSession(session);
  return session.actor;
}

export function refreshErpSession() {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function restoreErpSession() {
  // Nao sonda o auth central em instalacoes que ainda usam somente o login
  // local. A integracao futura precisa primeiro criar uma sessao recuperavel.
  if (!hasRecoverableErpSession()) return null;
  try {
    if (!hasErpSession()) await refreshErpSession();
    const actor = await request('/auth/me', { authenticated: true });
    currentSession = { ...currentSession, actor };
    return actor;
  } catch (error) {
    if (error?.code === 'session_replaced') throw error;
    if (error?.status === 401 || error?.code === 'session_not_recoverable' || error?.code === 'invalid_session_response') {
      clearErpSession();
      return null;
    }
    throw error;
  }
}

export async function meErpSession() {
  const actor = await request('/auth/me', { authenticated: true });
  if (!actor?.tenant?.id || !actor?.user?.id) {
    throw new ErpSessionError('A sessao central devolveu uma identidade invalida.', {
      code: 'invalid_session_identity'
    });
  }
  currentSession = { ...currentSession, actor };
  return actor;
}

export const getErpSessionActor = meErpSession;

export async function updateErpBusinessProfile(businessProfile) {
  await request('/tenant/business-profile', {
    method: 'PUT',
    body: { businessProfile: String(businessProfile || '').trim().toLowerCase() },
    authenticated: true
  });
  return meErpSession();
}

export function heartbeatErpSession() {
  return transientAvailabilityRequest('/auth/heartbeat', {
    method: 'POST',
    authenticated: true
  });
}

// Superficie operacional central compartilhada pelo runtime web. O renderer
// nunca recebe refresh token e tambem nao pode escolher tenant: ambos continuam
// vinculados a sessao autenticada pelo servidor.
export function submitErpSyncCommand(command) {
  return request('/sync/commands', {
    method: 'POST',
    body: command,
    authenticated: true
  });
}

export function pullErpSyncEvents({
  after = '0',
  limit = 100,
  waitMs = 0,
  signal
} = {}) {
  const query = new URLSearchParams({
    after: String(after || '0'),
    limit: String(limit || 100),
    waitMs: String(waitMs || 0)
  });
  return transientAvailabilityRequest(`/sync/events?${query.toString()}`, {
    authenticated: true,
    signal
  });
}

export function acknowledgeErpSyncCursor(cursor) {
  return request('/sync/cursor/ack', {
    method: 'POST',
    body: { cursor: String(cursor || '0') },
    authenticated: true
  });
}

export function getErpSyncState() {
  return request('/sync/state', { authenticated: true });
}

export function getErpSyncSnapshot() {
  return request('/sync/snapshot', { authenticated: true });
}

const ERP_FISCAL_BASE = '/fiscal';

function fiscalDocumentPath(documentId) {
  const normalized = String(documentId || '').trim();
  if (!/^[0-9a-f-]{36}$/i.test(normalized)) {
    throw new ErpSessionError('Informe um documento fiscal valido.', {
      code: 'invalid_fiscal_document_id'
    });
  }
  return `${ERP_FISCAL_BASE}/documents/${encodeURIComponent(normalized)}`;
}

export function getErpFiscalBootstrap() {
  return request(`${ERP_FISCAL_BASE}/bootstrap`, { authenticated: true });
}

export function listErpFiscalDocuments(limit = 50) {
  const acceptedLimit = Math.min(Math.max(Number(limit) || 50, 1), 100);
  return request(`${ERP_FISCAL_BASE}/documents?limit=${acceptedLimit}`, {
    authenticated: true
  });
}

export function getErpFiscalDocument(documentId) {
  return request(fiscalDocumentPath(documentId), { authenticated: true });
}

export function issueErpFiscalDocument(payload = {}) {
  const idempotencyKey = String(payload.idempotencyKey || '').trim();
  const body = { ...payload };
  delete body.idempotencyKey;
  return request(`${ERP_FISCAL_BASE}/documents`, {
    method: 'POST',
    body,
    authenticated: true,
    extraHeaders: { 'X-Idempotency-Key': idempotencyKey }
  });
}

export function syncErpFiscalDocument(documentId) {
  return request(`${fiscalDocumentPath(documentId)}/sync`, {
    method: 'POST',
    body: {},
    authenticated: true
  });
}

export function cancelErpFiscalDocument(documentId, details = {}) {
  return request(`${fiscalDocumentPath(documentId)}/cancel`, {
    method: 'POST',
    body: {
      reason: String(details.reason || ''),
      code: String(details.code || '') || null
    },
    authenticated: true
  });
}

export function configureErpFiscal(payload = {}) {
  return request(`${ERP_FISCAL_BASE}/configuration`, {
    method: 'PATCH',
    body: payload,
    authenticated: true
  });
}

export function uploadErpFiscalCertificate(payload = {}) {
  return request(`${ERP_FISCAL_BASE}/certificates`, {
    method: 'POST',
    body: payload,
    authenticated: true
  });
}

const ERP_CONECTA_BASE = '/conecta';

export function getErpConectaConfig() {
  return request(`${ERP_CONECTA_BASE}/configuration`, { authenticated: true });
}

export function saveErpConectaConfig(payload = {}) {
  return request(`${ERP_CONECTA_BASE}/configuration`, {
    method: 'PUT',
    body: {
      endpointUrl: payload.endpointUrl,
      tenantId: payload.tenantId,
      instanceId: payload.instanceId,
      token: payload.token
    },
    authenticated: true
  });
}

export function completeErpConectaAuthorization(authorizationRequest) {
  return request(`${ERP_CONECTA_BASE}/oauth/complete`, {
    method: 'POST',
    body: {
      request: String(authorizationRequest || '').trim()
    },
    authenticated: true
  });
}

export function clearErpConectaConfig() {
  return request(`${ERP_CONECTA_BASE}/configuration/remove`, {
    method: 'DELETE',
    authenticated: true
  });
}

export function healthErpConecta() {
  return request(`${ERP_CONECTA_BASE}/health`, { authenticated: true });
}

export function requestErpConecta(method, route, payload) {
  return request(`${ERP_CONECTA_BASE}/proxy`, {
    method: 'POST',
    body: { method, route, payload },
    authenticated: true
  });
}

function supportTicketPath(id) {
  const normalized = String(id ?? '').trim();
  if (!normalized) {
    throw new ErpSessionError('Informe um chamado valido.', {
      code: 'invalid_support_ticket_id'
    });
  }
  return `/support/tickets/${encodeURIComponent(normalized)}`;
}

function supportIdempotencyKey(operation) {
  const suffix = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  return `erp-support-${operation}-${suffix}`;
}

// Chamados pertencem sempre ao tenant da sessao autenticada. Estes metodos
// montam corpos por lista permitida para que nenhum caller consiga escolher ou
// sobrescrever tenantId pelo renderer.
export function listErpSupportTickets() {
  return request('/support/tickets', { authenticated: true });
}

export function createErpSupportTicket({ subject, description, priority } = {}) {
  return request('/support/tickets', {
    method: 'POST',
    body: { subject, description, priority },
    authenticated: true,
    extraHeaders: { 'X-Idempotency-Key': supportIdempotencyKey('create') }
  });
}

export async function getErpSupportTicket(id) {
  return request(supportTicketPath(id), { authenticated: true });
}

export async function replyErpSupportTicket(id, message) {
  return request(`${supportTicketPath(id)}/messages`, {
    method: 'POST',
    body: { message },
    authenticated: true,
    extraHeaders: { 'X-Idempotency-Key': supportIdempotencyKey('reply') }
  });
}

export async function logoutErpSession() {
  try {
    if (!hasErpSession() && hasRecoverableErpSession()) await refreshErpSession();
    if (hasErpSession()) {
      await request('/auth/logout', {
        method: 'POST',
        authenticated: true,
        retryAfterRefresh: false
      });
    }
  } finally {
    clearErpSession();
  }
}

export function getErpHeartbeatInterval() {
  const configured = Number(currentSession?.heartbeatIntervalMs || 0);
  // O servidor informa o teto adequado ao lease. Para que um login substituto
  // derrube a tela anterior rapidamente, o cliente nunca espera mais de 30 s.
  return configured > 0 ? Math.max(10_000, Math.min(30_000, configured)) : 30_000;
}

// No navegador, o App inicia este heartbeat depois de restaurar ou criar a
// sessao central. No Electron o processo principal executa o heartbeat e o
// renderer apenas recebe eventos sem tokens.
export function startErpSessionHeartbeat({ onError } = {}) {
  if (!hasErpSession()) return () => {};
  let stopped = false;
  let timer = null;
  const schedule = () => {
    if (!stopped && hasErpSession()) timer = setTimeout(run, getErpHeartbeatInterval());
  };
  const run = async () => {
    try {
      await heartbeatErpSession();
    } catch (error) {
      if (typeof onError === 'function') onError(error);
      if (error?.code === 'session_replaced' || !hasErpSession()) {
        stopped = true;
        return;
      }
    }
    schedule();
  };
  schedule();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

export const __erpSessionTestUtils = {
  ERP_API_BASE,
  ERP_DEVICE_KEY,
  ERP_CSRF_KEY,
  normalizeSession,
  errorCode,
  dropMemorySession() {
    currentSession = null;
  },
  reset() {
    currentSession = null;
    refreshPromise = null;
    replacementNotified = false;
    const storage = getSessionStorage();
    storage?.removeItem(ERP_DEVICE_KEY);
    storage?.removeItem(ERP_CSRF_KEY);
  }
};
